import { z } from 'zod';
import { isoTimestamp, severity, uuid } from './primitives.js';
/**
 * A device is the iOS or macOS app's own view of its push channel. The web UI
 * lets an admin paste a device token by hand; the app registers itself here
 * and then owns how loud its own notifications are allowed to be.
 */
/**
 * How a notification is allowed to arrive.
 *
 * - `critical` plays a sound even when the ring switch is off or a Focus is
 *   active. On iOS this needs Apple's Critical Alerts entitlement.
 * - `normal` is an ordinary banner with sound, suppressed by Focus as usual.
 * - `silent` delivers quietly to the notification centre.
 */
export const DELIVERY_MODES = ['silent', 'normal', 'critical'];
export const deliveryMode = z.enum(DELIVERY_MODES);
/** Higher is louder. Caps are applied by taking the minimum. */
export const DELIVERY_MODE_RANK = {
    silent: 0,
    normal: 1,
    critical: 2,
};
export const DEVICE_PLATFORMS = ['ios', 'macos'];
export const devicePlatform = z.enum(DEVICE_PLATFORMS);
/** `HH:MM` in 24 hour form. */
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit als HH:MM erwartet');
/**
 * A window that caps how loud notifications may be. Crossing midnight is
 * allowed: `22:00` to `06:00` means the night, not an empty range.
 */
export const quietHours = z.object({
    from: clockTime,
    to: clockTime,
    /** IANA zone, so the window means the same thing after a flight. */
    timezone: z.string().min(1).max(64).default('Europe/Berlin'),
    max_mode: deliveryMode.default('normal'),
});
/**
 * Which sound a critical notification plays on this device.
 *
 * `default` is whatever the server knows as `APNS_CRITICAL_SOUND`, `wilhelm`
 * ships in the app bundle, and a UUID names a sound an admin uploaded (see
 * `sounds.ts`). iOS silently falls back to its stock sound when a named file
 * is missing, so the server only names an uploaded sound once the device has
 * reported it installed.
 */
export const BUILTIN_CRITICAL_SOUNDS = ['default', 'wilhelm'];
export const criticalSound = z.union([z.enum(BUILTIN_CRITICAL_SOUNDS), uuid]);
export const CRITICAL_SOUND_FILES = {
    wilhelm: 'wilhelm.caf',
};
export function isUploadedSound(choice) {
    return !BUILTIN_CRITICAL_SOUNDS.includes(choice);
}
export const deliveryPolicy = z.object({
    /** At or above this severity a notification may be critical. */
    critical_from: severity.default('critical'),
    quiet_hours: quietHours.nullish(),
    critical_sound: criticalSound.default('default'),
});
/**
 * Reported by the app's Focus filter. iOS never tells an app which Focus is
 * on; it tells the app which settings the user picked for this app in that
 * Focus. `mode_cap` is exactly that choice, and the server caps against it.
 */
export const focusState = z.object({
    active: z.boolean(),
    mode_cap: deliveryMode.nullish(),
    /** Free label the user gave the filter, for display only. */
    focus_name: z.string().max(120).nullish(),
    reported_at: isoTimestamp,
});
export const device = z.object({
    id: uuid,
    user_id: uuid,
    platform: devicePlatform,
    label: z.string(),
    is_active: z.boolean(),
    /** Last eight characters of the APNs token. The full token never leaves the API. */
    token_tail: z.string(),
    delivery_policy: deliveryPolicy,
    focus: focusState.nullable(),
    /** Uploaded sounds the app reported as present in its `Library/Sounds`. */
    installed_sounds: z.array(uuid),
    last_success_at: isoTimestamp.nullable(),
    last_error: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
/**
 * Registration is idempotent on the device token: a reinstall or a token
 * refresh updates the existing row instead of piling up dead devices.
 */
export const registerDeviceBody = z.object({
    platform: devicePlatform.default('ios'),
    device_token: z
        .string()
        .regex(/^[0-9a-fA-F]{64,200}$/, 'Ungültiges APNs Device Token (Hex erwartet)'),
    label: z.string().min(1).max(120),
    bundle_id: z.string().min(3).max(200).nullish(),
    app_version: z.string().max(40).nullish(),
});
export const updateDeviceBody = z
    .object({
    label: z.string().min(1).max(120),
    is_active: z.boolean(),
    delivery_policy: deliveryPolicy,
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Mindestens ein Feld erforderlich' });
export const putFocusStateBody = z.object({
    active: z.boolean(),
    mode_cap: deliveryMode.nullish(),
    focus_name: z.string().max(120).nullish(),
});
/** Replaces the whole list: what the app found on disk after its last sync. */
export const putInstalledSoundsBody = z.object({
    sound_ids: z.array(uuid).max(500),
});
/** Answers "what would this alert sound like on my phone right now?". */
export const deliveryModePreview = z.object({
    mode: deliveryMode,
    reason: z.string(),
    /** The file a critical notification would name, and why that one. */
    critical_sound: z.object({ file_name: z.string(), reason: z.string() }),
});
//# sourceMappingURL=devices.js.map